/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button, Text, theme } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { currencyOptions } from "~/constants/currencyList";
import { RouteParams } from "~/models";
import {
  checkValue,
  generateActionSuccessText,
  isValidPrice,
  isValidUrlSchema,
  itHasNotWhitespace,
  useAxios,
} from "~/tools";
import { AssignedProductItem } from "./types";

type EditProductPricingProps = {
  product?: AssignedProductItem;
  refetch?: () => void;
};

type Values = {
  fixedFee: string;
  pricing: string;
  currency: string;
};

function EditProductPricing({ product, refetch }: EditProductPricingProps) {
  const { customerId } = useParams<RouteParams>();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const methods = useForm();

  const onSubmit = async (values: Values) => {
    setIsLoading(true);
    const organizedValues = {
      fixedFee: checkValue(values.fixedFee),
      pricing: checkValue(values.pricing),
      currency: checkValue(values.currency),
    };
    try {
      await Axios().put(
        `/customers/${customerId}/products/${product?.id}`,
        organizedValues
      );
      showSuccess(generateActionSuccessText(`Product Price`)()(`added`)());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
      setIsSidebarOpen(false);
      setIsLoading(false);
    }
  };

  const clean = () => {
    refetch && refetch();
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        type="button"
        size="sm"
        severity="medium"
        icon="edit"
        css={{ marginRight: "0.15rem" }}
      >
        Edit Pricing
      </Button>

      <SidebarForm
        title="Edit pricing"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <FlexContainer direction="column" align="flex-start">
          <Text tag="div" color={theme.color.text.text02}>
            Name:
          </Text>
          <Text weight="medium">{product?.name}</Text>
        </FlexContainer>

        <SidebarTextField
          name="pricing"
          label="Pricing"
          tooltip="Link to a spreadsheet with pricing formula for this Customer"
          required
          defaultValue={product?.pricing || ""}
          registerOptions={{
            validate: (link: string) =>
              itHasNotWhitespace(link) &&
              isValidUrlSchema.safeParse(link).success,
          }}
        />

        <SidebarTextField
          name="fixedFee"
          label="Fixed Fee"
          inputType="number"
          defaultValue={product?.fixedFee || ""}
          registerOptions={{ validate: isValidPrice }}
        />

        <SidebarDropdown
          name="currency"
          label="Currency"
          options={currencyOptions}
          defaultValue={product?.currency || product?.effectiveCurrency}
        />

        <SidebarFormButtons
          onCancel={() => setIsSidebarOpen(false)}
          submitEnabled={methods.watch("pricing")}
          submitLoading={isLoading}
        />
      </SidebarForm>
    </>
  );
}

export { EditProductPricing };
